import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Sleep timer for background radio. Consumers set a duration in minutes;
 * `remainingSeconds` counts down; when it hits 0, the consumer clears
 * whichever station it was playing.
 *
 * Timer state is intentionally *not* persisted — a fresh app open should
 * not carry a stale timer.
 */

interface SleepCtx {
  /** Minutes chosen, or 0 for "off". */
  minutes: number;
  /** Seconds remaining, or 0 when the timer isn't running. */
  remainingSeconds: number;
  /** true only in the transient tick when the timer just hit 0. */
  didExpire: boolean;
  /** Start / replace the current timer. Pass 0 to cancel. */
  set: (mins: number) => void;
  cancel: () => void;
  /** Consume the "just-expired" flag so it only fires once per expiry. */
  acknowledgeExpiry: () => void;
}

const Ctx = createContext<SleepCtx | undefined>(undefined);

export function SleepTimerProvider({ children }: { children: ReactNode }) {
  const [minutes, setMinutes] = useState(0);
  const [remainingSeconds, setRemaining] = useState(0);
  const [didExpire, setDidExpire] = useState(false);

  // Tick the countdown once a second while a timer is running.
  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const t = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          setDidExpire(true);
          setMinutes(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [remainingSeconds]);

  const set = useCallback((mins: number) => {
    setDidExpire(false);
    if (mins <= 0) {
      setMinutes(0);
      setRemaining(0);
      return;
    }
    setMinutes(mins);
    setRemaining(mins * 60);
  }, []);

  const cancel = useCallback(() => set(0), [set]);
  const acknowledgeExpiry = useCallback(() => setDidExpire(false), []);

  const value = useMemo<SleepCtx>(
    () => ({ minutes, remainingSeconds, didExpire, set, cancel, acknowledgeExpiry }),
    [minutes, remainingSeconds, didExpire, set, cancel, acknowledgeExpiry]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSleepTimer(): SleepCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSleepTimer must be used inside <SleepTimerProvider>");
  return v;
}

export function formatSleepRemaining(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
